import { currentPostedDate } from '@fruitful/fruitils';
import { subDays } from 'date-fns';
import { Redirect } from 'expo-router';
import { capitalize } from 'lodash';
import * as React from 'react';
import { useState } from 'react';
import {
    DateRangeSelectionSheet,
    LabelRegular,
    PillButton,
    ScreenContainer,
    SegmentedControl,
    XStack,
    YStack,
} from 'smoothie';

import { format, tomorrow } from '../../../../common/utils/dateUtils';
import { captureMessage } from '../../../../logger/Logger';
import useGetAccountDisplayDetailsFromGlobalState from '../../../hooks/useGetAccountDisplayDetailsFromGlobalState';
import { AccountTypeFilter } from '../../../types/Transactions';
import TransactionTable from './transactionTable/TransactionTable';

interface TransactionsProps {
    bankAccountId: string;
    syncteraAccountId: string;
}

const Transactions = ({
    bankAccountId,
    syncteraAccountId,
}: TransactionsProps) => {
    const defaultToDate = currentPostedDate();
    const defaultFromDate = subDays(defaultToDate, 120);

    const [type, setType] = useState<AccountTypeFilter>(AccountTypeFilter.ALL);
    const [toDate, setToDate] = useState(defaultToDate);
    const [fromDate, setFromDate] = useState(defaultFromDate);
    const [isDateSelectionSheetOpen, setIsDateSelectionSheetOpen] =
        useState(false);

    const toggleOptions = Object.values(AccountTypeFilter).map((type) => ({
        value: type,
        label: capitalize(type),
    }));

    const { account } = useGetAccountDisplayDetailsFromGlobalState({
        syncteraAccountId,
    });

    if (account == null) {
        // Not using an exception here since this fires extra because of re-renders (but does not noticeably impact UI)
        captureMessage(
            `Could not find account for transactions screen - syncteraAccountId: ${syncteraAccountId}`,
        );
        return <Redirect href="/home/banking" />;
    }

    return (
        <ScreenContainer title="All transactions" hasBackButton={true}>
            <YStack>
                {account.chargeSecuredSyncteraAccountId != null && (
                    <SegmentedControl
                        selected={type}
                        defaultValue={AccountTypeFilter.ALL}
                        onValueChange={(value) => {
                            setType(value);
                        }}
                        options={toggleOptions}
                    />
                )}
                <XStack gap="$4" justifyContent="space-between">
                    <PillButton
                        text="Filter by date range"
                        onPress={() => {
                            setIsDateSelectionSheetOpen(true);
                        }}
                        pillProps={{
                            alignSelf: 'flex-start',
                            marginVertical: 12,
                        }}
                    />
                    <LabelRegular
                        fontSize={14}
                        justifyContent="center"
                        alignSelf="center"
                    >
                        {`${format(fromDate, 'MMM d, yyyy')} - ${format(toDate, 'MMM d, yyyy')}`}
                    </LabelRegular>
                </XStack>
                <TransactionTable
                    bankAccountId={bankAccountId}
                    syncteraAccountId={syncteraAccountId}
                    allowPagination={true}
                    fromDate={fromDate}
                    toDate={toDate}
                    type={type}
                />
            </YStack>
            <DateRangeSelectionSheet
                maximumDate={tomorrow()}
                defaultFromDate={fromDate ?? defaultFromDate}
                defaultToDate={toDate ?? defaultToDate}
                isOpen={isDateSelectionSheetOpen}
                onClose={() => {
                    setIsDateSelectionSheetOpen(false);
                }}
                confirmButtonText="View transactions"
                onConfirm={(fromDate: Date, toDate: Date) => {
                    setFromDate(fromDate);
                    setToDate(toDate);
                    setIsDateSelectionSheetOpen(false);
                }}
            />
        </ScreenContainer>
    );
};

export default Transactions;
